import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Curaprox: pointers that cannot hover (touch tablets/phones). The desktop mega-menu is
 * hover-driven, so these devices need an explicit tap trigger — see `#onMenuClick`.
 * @type {MediaQueryList}
 */
const COARSE_POINTER = matchMedia('(hover: none), (pointer: coarse)');

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Curaprox keep-open (client feedback 2026-07): the menu no longer closes on
    // pointerleave, so wire up the explicit close paths (✕ toggle click, Esc, click
    // outside) and the arrow→✕ toggle state (data-cursor-outside on the header).
    this.addEventListener('click', this.#onMenuClick);
    document.addEventListener('keydown', this.#onKeydown);
    // Capture phase: fires before any page element under the transparent overlay can
    // stopPropagation() on its own click, so the outside-click close is never swallowed.
    document.addEventListener('click', this.#onDocumentClick, true);
    const hc = this.headerComponent;
    if (hc) {
      hc.addEventListener('pointerleave', this.#onHeaderPointerLeave);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);

    this.removeEventListener('click', this.#onMenuClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick, true);
    const hc = this.headerComponent;
    if (hc) {
      hc.removeEventListener('pointerleave', this.#onHeaderPointerLeave);
    }
    clearTimeout(this.#hoverIntentTimer);

    this.#cleanupMutationObserver();
  }

  /**
   * Curaprox keep-open: Esc closes an open panel.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#panelOpen) {
      this.#deactivate();
    }
  };

  /**
   * Curaprox keep-open: a click that lands OUTSIDE the header closes an open panel. The
   * transparent area to the right of the white card is `pointer-events: none`, so a click
   * there falls through to the page (outside the header) and closes the menu. Clicks inside
   * the header (nav links, the card, the ✕ toggle) are handled by their own behaviour.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#panelOpen) return;
    const target = event.target;
    // Keep the menu open ONLY for clicks on the interactive nav: the header bar, or the open
    // panel's white card (its `::before` white zone is pointer-events:auto so those clicks
    // target the `.menu-list__submenu` element). Every other click — the transparent overlay
    // (pointer-events:none, so the click falls through to the page) and anywhere else, i.e.
    // exactly where the close-X cursor shows — dismisses the menu.
    // `.header__navigation-bar-row` is the `navigation_bar` header variant's row — the nav can
    // live there instead of `.header__row--top` (sections/header.liquid), and without it a tap
    // on a nav link would be treated as an outside-click and close the panel this same click
    // is about to re-open.
    if (
      target instanceof Element &&
      target.closest('.header__row--top, .header__navigation-bar-row, .menu-list__submenu')
    ) {
      return;
    }
    this.#deactivate();
  };

  /**
   * Curaprox keep-open: clicking the ✕ toggle (a sibling of the nav link) closes the menu
   * instead of following the link.
   * @param {MouseEvent} event
   */
  #onMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('.menu-list__close-toggle');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      this.#deactivate();
      return;
    }

    // Curaprox (client 2026-07): the top-level nav items with a submenu (Shop / Mundgesundheit
    // / Entdecken) are mega-menu OPENERS only — a click must never navigate. They now render as
    // <button>s (blocks/_header-menu.liquid), plus the "More" overflow <button>; suppress the
    // default and toggle the panel for those. A CHILDLESS top-level item renders as a real
    // <a> with no submenu (no aria-haspopup) and must navigate normally — so bail before the
    // preventDefault for that case. The sub-menu links (`.mega-menu__link`) are untouched.
    const link = event.target.closest('.menu-list__link');
    if (!link) return;
    if (link.tagName === 'A' && !link.hasAttribute('aria-haspopup')) return;

    event.preventDefault();

    // Curaprox (client feedback 2026-08 — Jan Krammer): a CLICK on a top-level opener now
    // toggles its mega-menu open/closed on EVERY pointer type — it must never navigate, and
    // clicking is a first-class way to open the panel (not only hovering). Previously this
    // tap-toggle was gated to coarse/no-hover pointers (`if (!COARSE_POINTER.matches) return`),
    // so on desktop a click did nothing but suppress the default and the menu could only be
    // opened by hovering — which the client reported as "clicking does nothing / no way to
    // open by click". Desktop HOVER still opens via `activate` (the hover-intent path is
    // untouched and only runs for fine pointers); this just adds click as an explicit
    // toggle, and re-clicking the open item (or clicking while it is open) closes it.
    const listItem = link.closest('.menu-list__list-item');
    if (!listItem) return;

    // Clicking the item that is already open closes it.
    if (findMenuItem(listItem) === this.#state.activeItem) {
      this.#deactivate();
      return;
    }

    clearTimeout(this.#hoverIntentTimer);
    this.#openItem({ target: listItem });
  };

  /**
   * Curaprox keep-open: the cursor left the nav/panel area while a panel is open → reveal the
   * ✕ close control. STICKY: once set, `data-cursor-outside` is NOT cleared on re-enter (that
   * would hide the ✕ the instant the user moves back to click it) — it is cleared only when
   * the menu closes or switches to another item (see #deactivate / #openItem). The header's
   * descendants include the tall card, so this only fires once the pointer is off BOTH the
   * header bar and the white card.
   */
  #onHeaderPointerLeave = () => {
    if (this.#panelOpen) {
      this.headerComponent?.setAttribute('data-cursor-outside', '');
    }
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Curaprox: timer that clears the `data-menu-closing` flag once the menu background has
   * finished retracting (keeps the logo/nav colours dark while it's still behind them).
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #menuClosingTimer;

  /**
   * Curaprox (client feedback 2026-07): hover-intent timer. On pointer hover of a top-level
   * item, opening the mega-menu waits this long so the underline animation completes first
   * (on.com-style). Cancelled if the pointer leaves before the delay.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #hoverIntentTimer;

  /**
   * Curaprox (client feedback 2026-07): whether a mega-menu panel is currently pinned open.
   * When true the menu stays open on pointerleave and closes only on an explicit ✕ click,
   * Esc, or a click outside the panel.
   * @type {boolean}
   */
  #panelOpen = false;

  /**
   * Read a CSS time custom property (e.g. `360ms` / `0.36s`) as milliseconds.
   * @param {string} varName
   * @param {number} fallback
   * @returns {number}
   */
  #cssTimeMs(varName, fallback) {
    const raw = this.headerComponent
      ? getComputedStyle(this.headerComponent).getPropertyValue(varName).trim()
      : '';
    if (raw.endsWith('ms')) return parseFloat(raw) || fallback;
    if (raw.endsWith('s')) return parseFloat(raw) * 1000 || fallback;
    return fallback;
  }

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Curaprox hover-intent (client feedback 2026-07): schedule the mega-menu to open. On
   * pointer hover, wait for the underline animation (--nav-underline-speed) to complete
   * first so the underline reads as a "loading" cue and sweeping the cursor across the nav
   * doesn't fire accidental opens. Keyboard focus opens immediately. A pending open is
   * cancelled in `deactivate` if the pointer leaves first.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    // Curaprox (client feedback 2026-07-20): on a coarse/no-hover pointer the TAP handler in
    // `#onMenuClick` must be the ONLY thing that opens the panel, because every other opener races
    // that same tap and cancels it out. A single tap fires BOTH of the triggers below:
    //   • `pointerenter` — and Chrome applies a sticky `:hover` to the tapped element, which
    //     satisfies the hover-intent re-check further down;
    //   • `focus` — a tap focuses the link before the click, and the `<li>`'s `on:focus` binding is
    //     a CAPTURE listener, so focus on the descendant `<a>` reaches it (verified on the client's
    //     preview: dispatching focus at the anchor opens the panel).
    // Either one opens the panel, and then the SAME tap's click hits the tap-toggle and shuts it —
    // so the user sees nothing happen and has to tap a second time. Gate both here.
    // Keyboard is unaffected in practice: tabbing to an item no longer auto-opens it, but Enter on
    // the focused link fires a `click`, which `#onMenuClick` turns into a normal open.
    // Fine pointers skip this entirely, so desktop hover/focus behaviour is untouched.
    if (COARSE_POINTER.matches) return;

    if (event.type !== 'pointerenter') {
      // Keyboard focus (or any non-hover trigger) → open immediately.
      this.#openItem(event);
      return;
    }

    clearTimeout(this.#hoverIntentTimer);
    const delay = this.#cssTimeMs('--nav-underline-speed', 200);
    this.#hoverIntentTimer = setTimeout(() => {
      // Only open if the pointer is still resting on this item after the delay.
      if (item.closest('.menu-list__list-item')?.matches(':hover')) {
        this.#openItem(event);
      }
    }, delay);
  };

  /**
   * Activate (open) the selected menu item immediately.
   * Only `event.target` is read, so the tap path may pass a plain `{ target }` (see `#onMenuClick`).
   * @param {PointerEvent | FocusEvent | { target: EventTarget | null }} event
   */
  #openItem = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    // When a panel is already open and we're moving to another top-level item, mark
    // the menu as "switching" so the reveal animation is skipped (CSS reads this) —
    // the curtain should only play on the initial open, not on every link-to-link
    // hover. Reset to 'false' on full close in #deactivate so the next open animates.
    this.dataset.switching = previouslyActiveItem ? 'true' : 'false';

    // Curaprox: a fresh open cancels any pending close (e.g. re-hovering a nav item while the
    // panel is still wiping shut) so the colour-hold flag is cleared and the panel re-opens
    // cleanly instead of finishing its close.
    clearTimeout(this.#menuClosingTimer);
    this.headerComponent.removeAttribute('data-menu-closing');

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    // Curaprox: opening a mega-menu panel takes over the viewport — close the search
    // modal if it's open so there's a single overlay (the menu) instead of the menu
    // stacked on top of search. closeDialog() no-ops when the dialog is already closed.
    if (hasSubmenu) {
      /** @type {any} */
      const searchModal = document.querySelector('#search-modal');
      searchModal?.closeDialog?.();
    }

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    // Curaprox keep-open (client feedback 2026-07): a real panel is now open — pin it so
    // pointerleave no longer closes it (explicit ✕ / Esc / click-outside only). A fresh open
    // means the cursor is on the item, so clear the "cursor left the nav" flag (hides the ✕ until the cursor leaves again).
    if (submenu) {
      this.#panelOpen = true;
      this.headerComponent.dataset.menuOpen = '';
      this.headerComponent.removeAttribute('data-cursor-outside');
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Cancel a pending hover-intent open if the pointer leaves before the underline
    // finishes, so a quick sweep across items never opens anything.
    clearTimeout(this.#hoverIntentTimer);

    // Curaprox keep-open (client feedback 2026-07): once a panel is pinned open, leaving the
    // nav with the pointer must NOT close it — it closes only on an explicit ✕ click, Esc, or
    // a click outside. Keyboard `blur` keeps its original close behaviour (below) so tabbing
    // out of the menu still closes it for a11y.
    if (event.type === 'pointerleave' && this.#panelOpen) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Curaprox: the panel contains interactive controls that take focus and then release it —
    // most notably the locale <details> dropdowns (country / language) pinned to the bottom
    // of the card. Opening one, then clicking elsewhere, fires this `<li on:blur>`; keep the
    // menu open while the pointer is still over the card's INTERACTIVE area so focus churn
    // doesn't tear the panel down. (Explicit ✕ / Esc / click-outside remain the real close
    // paths under the keep-open model.)
    if (event.type === 'blur' && menu?.matches(':hover')) {
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    // Curaprox close (client feedback 2026-07): the panel wipes shut R→L via its own
    // clip-path, driven purely by the link's `aria-expanded` going false (below) — the old
    // full-width bar that needed a staggered two-phase height retract is gone, so this is a
    // single phase now. Capture overflow state before reset.
    const submenu = findSubmenu(item);
    const hadPanel = Boolean(submenu) || this.dataset.overflowExpanded === 'true';

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#setFullOpenHeaderHeight(0);

    if (hadPanel && this.headerComponent) {
      // While the panel wipes shut, hold the header content dark/legible so it doesn't flash
      // to the transparent home-hero colours mid-close; drop the hold once the wipe finishes.
      const hc = this.headerComponent;
      const speed = this.#cssTimeMs('--submenu-animation-speed', 360);
      hc.dataset.menuClosing = 'closing';
      clearTimeout(this.#menuClosingTimer);
      this.#menuClosingTimer = setTimeout(() => {
        hc.removeAttribute('data-menu-closing');
      }, speed);
    }

    // Curaprox keep-open: reset the pinned-open state and the ✕-icon flag.
    this.#panelOpen = false;
    this.headerComponent?.removeAttribute('data-menu-open');
    this.headerComponent?.removeAttribute('data-cursor-outside');
    clearTimeout(this.#hoverIntentTimer);

    // Menu is fully closing — clear the switching flag so the next initial open
    // (and this close) plays the reveal/curtain animation again.
    this.dataset.switching = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * The visible height of the header bar (top row in overlap situations, else the
   * whole component). Used both for the full-open height and to hold the white bar
   * during the close animation.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + this.#getHeaderVisibleHeight();

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
